import { useQuery } from '@tanstack/react-query';
import { ListApplicationsResponse, UniqueApplicationsListResource } from '@ab/ams-school-api/types/private/v1';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

export type Application = UniqueApplicationsListResource['attributes'] & { id: string };

const defaultValue: Application[] = [];

export function useApplicationsByPassport(
  passportId: string,
  currentApplicationId: string,
  options?: { onSuccess?: (apps: Application[], total: number) => void }
) {
  const request = useRequest();

  const {
    data = { applications: defaultValue, totalApplications: 0 },
    isLoading,
    isFetching,
    error,
    isError,
  } = useQuery<{ applications: Application[]; totalApplications: number }>(
    ['applications', passportId],
    async () => {
      const response = await request<ListApplicationsResponse>(
        `${config.apiHost}/private/v1/applications/passport/${passportId}`,
        { method: 'GET', isExpectedResponse }
      );
      const apps = response.data.map((app) => ({ ...app.attributes, id: app.id }));
      return {
        applications: apps.filter((app) => app.id !== currentApplicationId),
        totalApplications: response.meta?.pagination?.total ?? 0,
      };
    },
    {
      enabled: !!passportId,
      placeholderData: { applications: defaultValue, totalApplications: 0 },
      onSuccess: (payload) => {
        options?.onSuccess?.(payload.applications, payload.totalApplications);
      },
    }
  );

  return {
    applications: data.applications,
    applicationsError: error,
    isApplicationsError: isError,
    isFetchingApplications: isFetching,
    isLoadingApplications: isLoading,
    totalApplications: data.totalApplications,
  };
}

function isExpectedResponse(res: unknown): res is ListApplicationsResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res) && Array.isArray(res.data));
}
